import { useState } from 'react';
import Footer from '../components/Footer';

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  // Policy sections for sidebar navigation
  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'information-we-collect', title: 'Information We Collect' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'how-we-collect', title: 'How We Collect Information' },
    { id: 'auto-collected', title: 'Automatically Collected Information' },
    { id: 'how-we-use', title: 'How We Use Your Information' },
    { id: 'disclosure', title: 'Disclosure of Your Information' },
    { id: 'your-rights', title: 'Your Rights' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'childrens-privacy', title: 'Children\'s Privacy' },
    { id: 'changes', title: 'Changes to this Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-green-50 mt-[140px]">
      {/* Header */}
      <header className="bg-green-700 p-6 shadow-md">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
          <h2 className="text-xl text-green-100 mt-2">Maya Devi University</h2>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto flex flex-col md:flex-row gap-8 p-6 flex-grow">
        {/* Sidebar Navigation */}
        <aside className="md:w-1/4">
          <div className="bg-white p-4 rounded-lg shadow-md sticky top-6">
            <h2 className="text-xl font-semibold text-green-800 mb-4">Quick Navigation</h2>
            <nav>
              <ul className="space-y-2">
                {sections.map(section => (
                  <li key={section.id}>
                    <a 
                      href={`#${section.id}`}
                      className="block p-2 rounded hover:bg-green-100 text-green-700 hover:text-green-900 transition-colors"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="md:w-3/4">
          <div className="bg-white p-8 rounded-lg shadow-md">
            {/* Introduction Section */}
            <section id="introduction" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Privacy at Maya Devi University</h2>
              <p className="mb-4">
                Maya Devi University ("MDU", "we", "our", or "us") respects your privacy and is committed to protecting it through compliance with this policy.
              </p>
              <p className="mb-4">
                This Privacy Policy ("Policy") describes the types of information MDU may collect directly from you or your authorized representative, or from a third party authorized by MDU to provide services to you. It also outlines the information you may provide when using MDU's websites, services, and applications (collectively called "Services"). MDU collects such information by lawful and fair means only.
              </p>
              <p className="mb-4">
                This Policy does not apply to information collected by any third party, including through any application or content (such as advertising) that may link to or be accessible from or on MDU's Services.
              </p>
              <p className="mb-4">
                Please read this Policy carefully. By accessing or using our Services, you agree to this Policy. If you do not agree with our policies and practices, please do not use our Services. This Policy may change from time to time. Continued use of our Services after such changes means you accept those changes.
              </p>
            </section>

            {/* Information We Collect */}
            <section id="information-we-collect" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Information We Collect</h2>
              <p className="mb-4">
                MDU may collect your personal information, including but not limited to your bank details, passport details, educational background, and work experience to assist with university-related programs, including study abroad services, student visa assistance, letters of recommendation, and application processing.
              </p>
              <p className="mb-4">
                To access many MDU services, you may be required to register through official MDU portals and/or fill out online forms. MDU may request personal details including your name, email address, phone number, date of birth, and related academic or professional information.
              </p>
              <p className="mb-4">
                MDU does not collect sensitive personal information unless it is reasonably necessary for our academic or administrative operations.
              </p>
            </section>

            {/* Definitions */}
            <section id="definitions" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <span className="font-semibold">Personal Information (PI):</span> Any data that identifies you, directly or indirectly. This may include your name, contact details, date of birth, government-issued IDs, academic history, IP address, or other identifiers.
                </li>
                <li>
                  <span className="font-semibold">Non-Personal Information:</span> Data that does not identify you individually, such as browser details, device type, pages visited, and usage statistics.
                </li>
                <li>
                  <span className="font-semibold">Information:</span> Refers to both Personal and Non-Personal Information.
                </li>
              </ul>
            </section>

            {/* How We Collect Information */}
            <section id="how-we-collect" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">How We Collect Information</h2>
              <p className="mb-4">
                MDU collects Information in two primary ways:
              </p>
              <ol className="list-decimal pl-6 mb-4 space-y-2">
                <li>
                  <span className="font-semibold">Directly from you:</span> Through applications, email communications, form submissions, or registration on our platforms.
                </li>
                <li>
                  <span className="font-semibold">Automatically:</span> When you interact with our Services, such as browsing our website or using our mobile applications.
                </li>
              </ol>
              <p className="mb-4">
                You may provide Information when you:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Register for an account</li>
                <li>Submit applications or forms</li>
                <li>Subscribe to newsletters</li>
                <li>Enter university events or contests</li>
                <li>Communicate with us via email or social media platforms</li>
              </ul>
            </section>

            {/* Automatically Collected Information */}
            <section id="auto-collected" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Automatically Collected Information</h2>
              <p className="mb-4">
                When you access our Services, we may automatically collect data such as:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>IP address</li>
                <li>Browser and device type</li>
                <li>Session activity</li>
                <li>Pages visited</li>
                <li>Language preference</li>
                <li>Clickstream data</li>
                <li>Location information</li>
              </ul>
              <p>
                This information helps us improve our Services, track usage, analyze trends, and manage site performance. In some cases, we may combine this data with your Personal Information.
              </p>
            </section>

            {/* How We Use Your Information */}
            <section id="how-we-use" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">How We Use Your Information</h2>
              <p className="mb-4">
                MDU may use your Information for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>To operate and improve our websites and Services</li>
                <li>To provide the Services you request</li>
                <li>To communicate with you regarding updates, offers, and announcements</li>
                <li>To manage support services and respond to inquiries</li>
                <li>To send administrative and legal notifications</li>
                <li>To comply with applicable laws, regulations, or legal processes</li>
                <li>To prevent fraud and ensure security</li>
                <li>To analyze user trends and preferences</li>
              </ul>
              <p>
                You may opt out of receiving marketing emails by clicking "unsubscribe" in the emails or by contacting us at info@maya.edu.in.
              </p>
            </section>

            {/* Disclosure of Your Information */}
            <section id="disclosure" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Disclosure of Your Information</h2>
              <p className="mb-4">
                We may disclose your Personal Information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>To authorized MDU service providers who support us in delivering Services (e.g., IT, communications, analytics, and payment providers)</li>
                <li>In response to legal requirements or court orders</li>
                <li>To enforce our Terms of Service or protect MDU's rights, users, or the public</li>
                <li>With your consent</li>
              </ul>
              <p className="mb-4">
                Our service providers are contractually bound to protect your information and may use it only to support our services.
              </p>
              <p>
                By using our Services, you consent to be contacted via email, phone, SMS, WhatsApp, or other communication channels by MDU or its trusted partners regarding programs, services, or events.
              </p>
            </section>

            {/* Your Rights */}
            <section id="your-rights" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Your Rights</h2>
              <p className="mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Access your Personal Information</li>
                <li>Request corrections or updates</li>
                <li>Request deletion or anonymization</li>
                <li>Withdraw consent for specific uses</li>
              </ul>
              <p>
                Requests can be sent to info@maya.edu.in. We will retain your data as necessary to provide Services, maintain legal compliance, and fulfill administrative requirements.
              </p>
            </section>

            {/* Data Security */}
            <section id="data-security" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Data Security</h2>
              <p>
                We use reasonable technical and administrative safeguards to protect your data. However, please note that no method of internet transmission is entirely secure. By using our Services, you accept this risk.
              </p>
            </section>

            {/* Children's Privacy */}
            <section id="childrens-privacy" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Children's Privacy</h2>
              <p>
                We do not knowingly collect Personal Information from individuals under 16 years of age. If we discover such data has been collected, we will delete it promptly. If you believe a child has submitted information to us, please contact info@maya.edu.in.
              </p>
            </section>

            {/* Changes to this Policy */}
            <section id="changes" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Changes to this Policy</h2>
              <p>
                We reserve the right to update this Policy periodically. Any updates will be reflected by the "Last Modified" date and communicated through appropriate channels. Continued use of MDU Services after changes are made constitutes your acceptance of the revised Policy.
              </p>
            </section>

            {/* Contact Us */}
            <section id="contact" className="mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Contact Us</h2>
              <p className="mb-4">
                For any questions, concerns, or requests regarding this Policy or your personal data, please contact:
              </p>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="font-semibold">Maya Devi University</p>
                <p>Email: <a href="mailto:info@maya.edu.in" className="text-green-700 underline">info@maya.edu.in</a></p>
              </div>
            </section>

            {/* Last Updated */}
            <div className="mt-8 pt-4 border-t border-green-100 text-sm text-gray-500">
              <p>Last Modified: May 20, 2025</p>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <Footer/>
    </div>
  );
}